'use client';

import { useEffect, useId, useSyncExternalStore } from 'react';

import { createPortal } from 'react-dom';
import { css, cx } from 'styled-system/css';

/**
 * Chakra-free stand-in for `showToast` (utils/toast): same call shape for what the article page
 * uses (`{ status, title }`), same defaults (solid variant, bottom placement, 5 s). A module
 * store + a portal host (`ToastRegion`) rendered by the island that fires toasts.
 */

type ToastStatus = 'error' | 'success' | 'info' | 'warning';
type ToastItem = { id: number; status: ToastStatus; title: string; isClosing: boolean };
type ToastOptions = { status?: ToastStatus; title: string };

const DURATION_MS = 5000;
const EXIT_MS = 200;

let toasts: ToastItem[] = [];
let nextId = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const EMPTY: ToastItem[] = [];

export const showToast = ({ status = 'info', title }: ToastOptions) => {
  const id = nextId++;
  toasts = [...toasts, { id, status, title, isClosing: false }];
  emit();
  window.setTimeout(() => {
    toasts = toasts.map((toast) => (toast.id === id ? { ...toast, isClosing: true } : toast));
    emit();
    window.setTimeout(() => {
      toasts = toasts.filter((toast) => toast.id !== id);
      emit();
    }, EXIT_MS);
  }, DURATION_MS);
};

// Chakra v2 ToastProvider region (bottom) + Toast + solid Alert.
const regionClass = css({
  position: 'fixed',
  zIndex: 5500,
  pointerEvents: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '0 auto',
  padding: 0,
  left: 0,
  right: 0,
  bottom: 'env(safe-area-inset-bottom, 0px)',
});

const toastClass = css({
  pointerEvents: 'auto',
  maxWidth: '560px',
  minWidth: '300px',
  margin: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  px: 4,
  py: 3,
  borderRadius: 'md',
  boxShadow: 'lg',
  textAlign: 'start',
  color: 'white',
  animation: 'sh-more-toast-in 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&[data-closing]': { animation: 'sh-more-toast-out 0.2s cubic-bezier(0.4, 0, 1, 1) forwards' },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const statusClass: Record<ToastStatus, string> = {
  error: css({ bg: 'red.500' }),
  success: css({ bg: 'green.500' }),
  info: css({ bg: 'blue.500' }),
  warning: css({ bg: 'orange.500' }),
};

// Chakra's alert WarningIcon (error/warning) / InfoIcon / CheckIcon paths.
const ICON_PATHS: Record<ToastStatus, string> = {
  error:
    'M11.983,0a12.206,12.206,0,0,0-8.51,3.653A11.8,11.8,0,0,0,0,12.207,11.779,11.779,0,0,0,11.8,24h.214A12.111,12.111,0,0,0,24,11.791h0A11.766,11.766,0,0,0,11.983,0ZM10.5,16.542a1.476,1.476,0,0,1,1.449-1.53h.027a1.527,1.527,0,0,1,1.523,1.47,1.475,1.475,0,0,1-1.449,1.53h-.027A1.529,1.529,0,0,1,10.5,16.542ZM11,12.5v-6a1,1,0,0,1,2,0v6a1,1,0,1,1-2,0Z',
  warning:
    'M11.983,0a12.206,12.206,0,0,0-8.51,3.653A11.8,11.8,0,0,0,0,12.207,11.779,11.779,0,0,0,11.8,24h.214A12.111,12.111,0,0,0,24,11.791h0A11.766,11.766,0,0,0,11.983,0ZM10.5,16.542a1.476,1.476,0,0,1,1.449-1.53h.027a1.527,1.527,0,0,1,1.523,1.47,1.475,1.475,0,0,1-1.449,1.53h-.027A1.529,1.529,0,0,1,10.5,16.542ZM11,12.5v-6a1,1,0,0,1,2,0v6a1,1,0,1,1-2,0Z',
  info: 'M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm.25,5a1.5,1.5,0,1,1-1.5,1.5A1.5,1.5,0,0,1,12.25,5ZM14.5,18.5h-4a1,1,0,0,1,0-2h.75a.25.25,0,0,0,.25-.25v-4.5a.25.25,0,0,0-.25-.25H10.5a1,1,0,0,1,0-2h1a2,2,0,0,1,2,2v4.75a.25.25,0,0,0,.25.25h.75a1,1,0,1,1,0,2Z',
  success:
    'M12,0A12,12,0,1,0,24,12,12.014,12.014,0,0,0,12,0Zm6.927,8.2-6.845,9.289a1.011,1.011,0,0,1-1.43.188L5.764,13.769a1,1,0,1,1,1.25-1.562l4.076,3.261,6.227-8.451A1,1,0,1,1,18.927,8.2Z',
};

// Several islands on one page may each render a ToastRegion (e.g. the article's newsletter form
// and the quiz): only the first mounted one renders, so a toast never shows twice.
let regionOwners: string[] = [];
const regionListeners = new Set<() => void>();
const subscribeRegions = (listener: () => void) => {
  regionListeners.add(listener);
  return () => regionListeners.delete(listener);
};
const emitRegions = () => regionListeners.forEach((listener) => listener());

/** Portal host for `showToast`; render inside the island that uses it (duplicates stay inert). */
export const ToastRegion = () => {
  const regionId = useId();
  useEffect(() => {
    regionOwners = [...regionOwners, regionId];
    emitRegions();
    return () => {
      regionOwners = regionOwners.filter((owner) => owner !== regionId);
      emitRegions();
    };
  }, [regionId]);
  const items = useSyncExternalStore(
    subscribe,
    () => toasts,
    () => EMPTY,
  );
  // false on the server and until this region registered (client-only, like the former isClient)
  const isOwner = useSyncExternalStore(
    subscribeRegions,
    () => regionOwners[0] === regionId,
    () => false,
  );
  if (!isOwner || items.length === 0) return null;

  return createPortal(
    <ul className={regionClass} aria-live="polite" role="region" aria-label="Notifications">
      {items.map((toast) => (
        <li key={toast.id} className={css({ listStyle: 'none' })}>
          <div
            role={toast.status === 'error' ? 'alert' : 'status'}
            className={cx(toastClass, statusClass[toast.status])}
            data-closing={toast.isClosing ? '' : undefined}
          >
            <span className={css({ flexShrink: 0, marginEnd: 3, w: 5, h: 6, display: 'inherit' })}>
              <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor" aria-hidden focusable="false">
                <path d={ICON_PATHS[toast.status]} />
              </svg>
            </span>
            <div className={css({ flex: '1', maxWidth: '100%' })}>
              <div className={css({ fontWeight: 'bold', lineHeight: 6, marginEnd: 2 })}>{toast.title}</div>
            </div>
          </div>
        </li>
      ))}
    </ul>,
    document.body,
  );
};
